/**
 * Prize ranks: which (main numbers matched, bonus matched) pair earns which rank,
 * and how likely each is.
 *
 * The mapping is not documented in the archives — they publish "rang 3" without
 * saying what rang 3 means — so it was inferred and checked. For every draw, the
 * number of winners at a rank equals the ticket volume times the probability of the
 * corresponding outcome, and the probability is pure combinatorics. A wrong mapping
 * makes the volume estimates from different ranks disagree by orders of magnitude.
 * Across 2 099 draws the estimates agree to within a factor of 1,4 to 1,5, which is
 * what noise on small winner counts produces. `npm run rtp` prints that spread on
 * every run, so the mapping stays falsifiable rather than merely asserted.
 *
 * The Loto's last rank is the refund — the chance number with fewer than two main
 * numbers — so it covers two outcomes at once.
 */
import { GAMES, binomial } from './games';
import type { GameKey } from './games';

export interface RankSpec {
  readonly rank: number;
  /** Main numbers matched. A list when one rank covers several outcomes. */
  readonly main: readonly number[];
  readonly bonus: number;
  readonly label: string;
}

const loto = (rank: number, main: number[], bonus: number): RankSpec => ({
  rank,
  main,
  bonus,
  label:
    main.length > 1
      ? 'N° Chance seul'
      : `${main[0]} numéro${(main[0] as number) > 1 ? 's' : ''}${bonus === 1 ? ' + N° Chance' : ''}`,
});

const em = (rank: number, main: number, stars: number): RankSpec => ({
  rank,
  main: [main],
  bonus: stars,
  label: `${main} numéro${main > 1 ? 's' : ''} + ${stars} étoile${stars > 1 ? 's' : ''}`,
});

const dreams = (rank: number, main: number, bonus: number): RankSpec => ({
  rank,
  main: [main],
  bonus,
  label: `${main} numéro${main > 1 ? 's' : ''}${bonus === 1 ? ' + N° Dream' : ''}`,
});

export const RANKS: { readonly [K in GameKey]: readonly RankSpec[] } = {
  loto: [
    loto(1, [5], 1),
    loto(2, [5], 0),
    loto(3, [4], 1),
    loto(4, [4], 0),
    loto(5, [3], 1),
    loto(6, [3], 0),
    loto(7, [2], 1),
    loto(8, [2], 0),
    loto(9, [0, 1], 1),
  ],
  em: [
    em(1, 5, 2),
    em(2, 5, 1),
    em(3, 5, 0),
    em(4, 4, 2),
    em(5, 4, 1),
    em(6, 3, 2),
    em(7, 4, 0),
    em(8, 2, 2),
    em(9, 3, 1),
    em(10, 3, 0),
    em(11, 1, 2),
    em(12, 2, 1),
    em(13, 2, 0),
  ],
  eurodreams: [
    dreams(1, 6, 1),
    dreams(2, 6, 0),
    dreams(3, 5, 0),
    dreams(4, 4, 0),
    dreams(5, 3, 0),
    dreams(6, 2, 0),
  ],
};

/** The rank a ticket earns, or null when it wins nothing. */
export function rankFor(game: GameKey, mainHits: number, bonusHits: number): RankSpec | null {
  for (const spec of RANKS[game]) {
    if (spec.bonus === bonusHits && spec.main.includes(mainHits)) return spec;
  }
  return null;
}

/** P(exactly `mainHits` of the drawn main numbers and `bonusHits` of the bonus). */
export function outcomeProbability(
  game: GameKey,
  mainHits: number | readonly number[],
  bonusHits: number,
): number {
  const g = GAMES[game];
  const hits = typeof mainHits === 'number' ? [mainHits] : mainHits;
  let pMain = 0;
  for (const m of hits) {
    pMain += (binomial(g.k, m) * binomial(g.n - g.k, g.k - m)) / binomial(g.n, g.k);
  }
  const pBonus =
    (binomial(g.bk, bonusHits) * binomial(g.bmax - g.bk, g.bk - bonusHits)) /
    binomial(g.bmax, g.bk);
  return pMain * pBonus;
}

export function rankProbability(game: GameKey, rank: number): number {
  const spec = RANKS[game].find((r) => r.rank === rank);
  if (spec === undefined) return 0;
  return outcomeProbability(game, spec.main, spec.bonus);
}

/** P(a single grid wins anything at all). Loto ~1/6, EuroMillions ~1/13. */
export function winProbability(game: GameKey): number {
  let total = 0;
  for (const spec of RANKS[game]) total += outcomeProbability(game, spec.main, spec.bonus);
  return total;
}
